/*
 * decimal-quantity.js — seletor de metragem decimal na PDP (versão CBV app novo).
 * Reescrito enxuto: UMA chamada ao App Proxy por ação, com timeout (AbortController)
 * e degradação clara (nunca pendura a página). Casado ao markup do tema
 * (#add-to-cart-decimal + #quantidade-decimal). Núcleo `resolverCorte` compartilhado
 * com o carrinho via window.DecimalCut.
 */
(function () {
  'use strict';
  if (window.decimalQuantityInitialized) return;

  // ---------- helpers de número/moeda (BR) ----------
  const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
  function parseBR(v) {
    if (typeof v === 'number') return v;
    // Aceita PONTO ou VÍRGULA como separador decimal: "3.4" e "3,4" são ambos 3,4.
    // Antes o `.replace(/\./g, '')` removia todo ponto (tratando-o como separador de
    // milhar), então "3.4" digitado virava 34. Metragem nunca usa milhar (é no máximo
    // dezenas de metros), então tratar ponto como decimal é sempre o certo aqui.
    // Rastreia: ponto no seletor de metragem.
    const n = parseFloat(String(v ?? '').replace(',', '.').replace(/[^\d.]/g, ''));
    return Number.isFinite(n) ? Math.round(n * 10) / 10 : 0;
  }
  const fmtBR = (n) => (Number.isFinite(n) ? n : 0).toFixed(1).replace('.', ',');
  const brl = (n) => (Number.isFinite(n) ? n : 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

  // ---------- o corte só vai ao carrinho com o preço CERTO ----------
  // #6663 (12/10/2026): o app renomeou a "Reserva 1" para "6,5 metros" a R$ 77,74, e a
  // linha entrou no carrinho e fechou o pedido 14 min depois ainda como "Reserva 1" a
  // R$ 11,96 (reserva custa 1 metro). A sondagem só olhava "existe e está disponível",
  // e a reserva sempre está. Agora o proxy devolve o preço que a variante TEM que ter,
  // e nem a sondagem nem o /cart/add aceitam título de reserva ou preço diferente.
  const PRECO_ESPERADO = Object.create(null); // id numérico da variante → centavos
  const RESERVA_RE = /^reserva\s+\d+$/i;
  const ehReserva = (titulo) => RESERVA_RE.test(String(titulo || '').trim());
  const precoEsperado = (variantId) => PRECO_ESPERADO[String(variantId)];
  // Folga de 1 centavo: os dois lados arredondam metros × preço/metro com toFixed(2).
  const precoConfere = (variantId, centavos) => {
    const e = PRECO_ESPERADO[String(variantId)];
    return e == null || Math.abs(Number(centavos) - e) <= 1;
  };

  // ---------- núcleo: resolver a variante de corte (1 chamada, resiliente) ----------
  // Fala com o App Proxy na RAIZ (nossa rota-recurso atende). Re-tenta SÓ em falha
  // transitória (timeout/rede/5xx/429); 4xx falha na hora. Backend é idempotente por
  // metragem, então nunca cria duplicata ao re-tentar.
  async function resolverCorte(productIdNum, metros, titulo, precoMetro, opts = {}) {
    const { timeoutMs = 12000, maxTentativas = 3 } = opts;
    const body = {
      productId: `gid://shopify/Product/${productIdNum}`,
      quantidade: Number(metros).toFixed(1),
      titulo: titulo || '',
      precoMetro: Number(precoMetro),
    };
    let ultimoErro;
    for (let i = 1; i <= maxTentativas; i++) {
      const ctrl = new AbortController();
      const timer = setTimeout(() => ctrl.abort(), timeoutMs);
      try {
        const res = await fetch('/apps/qty-handler', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          credentials: 'same-origin',
          body: JSON.stringify(body),
          signal: ctrl.signal,
        });
        clearTimeout(timer);
        if (res.ok) {
          const data = await res.json().catch(() => null);
          const gid = data && data.success && data.variant && data.variant.id;
          if (gid) {
            const id = String(gid).split('/').pop();
            const preco = parseFloat(data.variant.price);
            if (preco > 0) PRECO_ESPERADO[id] = Math.round(preco * 100);
            return id;
          }
          throw new Error('resposta inválida do corte');
        }
        // 409 = o servidor conferiu o estoque de AGORA e não cabe. É resposta, não
        // falha: traz a frase pronta ("temos 2,0 m e o carrinho pede 3,0 m") e o
        // cliente precisa vê-la, não um código. Sem isto a página mostraria "corte
        // recusado (409)", que não explica nada e parece defeito do site.
        if (res.status === 409) {
          const d = await res.json().catch(() => null);
          const err = new Error((d && d.error) || 'Estoque insuficiente para esta metragem.');
          err.semEstoque = true;
          err.naoRetentar = true;
          throw err;
        }
        if (res.status < 500 && res.status !== 429) {
          const err = new Error(`corte recusado (${res.status})`);
          err.naoRetentar = true; // 4xx é decisão do servidor; repetir dá a mesma coisa
          throw err;
        }
        ultimoErro = new Error(`corte ${res.status}`); // 5xx/429 → transitório, re-tenta
      } catch (e) {
        clearTimeout(timer);
        ultimoErro = e; // abort (timeout) ou rede → re-tenta
        // O laço CATCH tudo, inclusive o que ele mesmo lançou — sem esta saída, uma
        // recusa de estoque era re-tentada 3× (mais duas chamadas inúteis e ~900 ms
        // de atraso) só para terminar na mesma recusa.
        if (e && e.naoRetentar) throw e;
      }
      if (i < maxTentativas) await sleep(300 * i);
    }
    throw ultimoErro || new Error('corte não resolvido');
  }

  // ---------- helpers de carrinho ----------
  // `cartPronto` evita reler o carrinho quando quem chama já o leu (a /cart pedia o
  // mesmo /cart.js duas vezes seguidas antes de começar o trabalho). Sem argumento, lê.
  async function getCartMeters(productIdNum, cartPronto) {
    try {
      const cart = cartPronto || await fetch('/cart.js', { headers: { Accept: 'application/json' } }).then((r) => r.json());
      let total = 0;
      for (const it of cart.items || []) {
        if (String(it.product_id) !== String(productIdNum)) continue;
        const q = it.properties && (it.properties._Quantidade || it.properties['_Quantidade']);
        // _Quantidade é gravado com PONTO ("1.0 metros"); NÃO usar parseBR (que trata
        // ponto como milhar → "1.0"→10). Parse decimal direto. Rastreia: bug estoque 10×.
        if (q) { const n = parseFloat(String(q).replace(',', '.').replace(/[^\d.]/g, '')); if (Number.isFinite(n)) total += n; }
      }
      return Math.round(total * 10) / 10;
    } catch { return 0; }
  }
  // Add ao carrinho com RETRY. Motivo: variante recém-criada leva alguns segundos
  // pra ficar comprável na vitrine (propagação) → /cart/add.js responde 422 até lá.
  // 422/429/5xx = transitório → espera e tenta de novo (backoff). Prioridade máxima:
  // o corte NUNCA pode falhar no add. Validado 21/07: 4.2m dava 422 na hora, 200 depois.
  async function cartAdd(variantIdNum, metros, precoMetro, sku) {
    /*
     * `SKU` SEM UNDERSCORE de propósito. Propriedade iniciada por `_` é escondida da
     * cliente pelo tema (ver o filtro em cart-drawer-decimal.liquid) e serviria só ao
     * admin. Aqui a intenção é o contrário: a cliente deve poder ler o SKU no
     * carrinho, no checkout e no e-mail, para citá-lo ao falar com o atendimento sobre
     * aquele tecido depois. Só entra quando existe — propriedade vazia poluiria a
     * linha sem informar nada.
     */
    const properties = { _Quantidade: `${Number(metros).toFixed(1)} metros`, 'Valor por metro': brl(precoMetro) };
    if (sku) properties.SKU = String(sku);

    const body = JSON.stringify({
      items: [{
        id: Number(variantIdNum),
        quantity: 1, // corte: sempre 1; a metragem vai nas properties
        properties,
      }],
    });
    // A sondagem (esperarVariantePronta) já garante que a variante propagou, então
    // isto é ~1 request. Em 429 (rate limit) PARAMOS na hora — retentar PROLONGA o
    // bloqueio. Só 422/5xx (variante ainda propagando/servidor) ganham 1 retry gentil.
    const DELAYS = [2500];
    let res;
    for (let i = 0; i <= DELAYS.length; i++) {
      res = await fetch('/cart/add.js', {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body,
      });
      if (res.ok) {
        const j = await res.json();
        // Segunda conferência, agora sobre a LINHA que o carrinho gravou: a sondagem
        // lê /products/{handle}.js, e o carrinho pode estar vendo outra foto. Linha
        // com título de reserva ou preço errado SAI do carrinho na hora — deixá-la
        // é vender o corte a preço de 1 metro.
        const it = j && Array.isArray(j.items) ? j.items[0] : null;
        if (it && (ehReserva(it.variant_title) || !precoConfere(variantIdNum, it.original_price))) {
          // Tira SÓ a unidade que este add pôs: se a variante já estava noutra linha com
          // as mesmas properties, a Shopify fundiu e zerar apagaria a linha que já existia.
          await fetch('/cart/change.js', {
            method: 'POST', headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ id: it.key, quantity: Math.max(0, (Number(it.quantity) || 1) - 1) }),
          }).catch(() => {});
          const e = new Error('preco-desatualizado');
          e.naoPropagou = true;
          throw e;
        }
        return j;
      }
      if (res.status === 429) {
        const ra = parseFloat(res.headers.get('Retry-After'));
        const e = new Error('rate-limit');
        e.rateLimited = true; e.retryAfter = ra > 0 ? Math.ceil(ra) : 30;
        throw e; // NÃO retenta: deixa o limite resetar
      }
      if (res.status === 422 || res.status >= 500) {
        if (i < DELAYS.length) await new Promise((r) => setTimeout(r, DELAYS[i]));
        continue;
      }
      break; // erro definitivo
    }
    throw new Error(`cart/add ${res ? res.status : 'sem resposta'}`);
  }
  // remove as demais linhas do MESMO produto (mantém a variante nova) — o corte novo já é o total acumulado
  //
  // PELA CHAVE, e só as linhas que entraram na conta (16/10/2026). Antes removia por
  // POSIÇÃO (`line:`) lendo o carrinho de novo: dois Comprar quase juntos liam a mesma
  // posição N e o 2º apagava o corte novo; e uma linha do mesmo tecido que entrasse
  // DEPOIS da leitura do total (drawer, outra aba, durante os segundos do
  // prepararCorte) era apagada sem ter sido somada — metros perdidos. `chavesAntes`
  // são as chaves lidas junto com o total; sem elas, cai no comportamento antigo de
  // pegar todas as do produto, mas ainda pela chave.
  async function removeOutras(productIdNum, exceptVariantIdNum, chavesAntes) {
    const cart = await fetch('/cart.js', { headers: { Accept: 'application/json' }, cache: 'no-store' }).then((r) => r.json());
    const linhas = (cart.items || []).filter((it) => String(it.product_id) === String(productIdNum)
      && String(it.variant_id) !== String(exceptVariantIdNum)
      && (!chavesAntes || chavesAntes.indexOf(it.key) !== -1));
    for (const it of linhas) {
      for (let t = 0; t < 2; t++) {
        const r = await fetch('/cart/change.js', {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: it.key, quantity: 0 }),
        }).catch(() => null);
        if (r && r.ok) break;
        await new Promise((res) => setTimeout(res, 1000));
      }
    }
  }

  // Espera a variante recém-criada aparecer no ÍNDICE da vitrine antes de tentar o
  // /cart/add. Sonda com GET /products/{handle}.js (barato, NÃO estoura o rate limit
  // do /cart/add como o POST fazia). Quando a variante aparece ali, o /cart/add aceita.
  // Retorna true se a variante ficou disponível (available:true) a tempo; false se
  // estourou o tempo. Sem handle, retorna true (não dá pra sondar; o retry cobre).
  async function esperarVariantePronta(handle, variantId, maxMs) {
    if (!handle || !variantId) return true;
    const alvo = String(variantId);
    // Teto de espera. Medido em 23/07/2026: propagação de variante NOVA no índice da
    // vitrine variou de ~2s a ~24s — o limite antigo de 25s passava raspando e viraria
    // "não propagou" (ação abortada) por poucos segundos. Agora que o pré-aquecimento e o
    // Confirmar compartilham a MESMA espera, o prazo passa a contar do clique no +, o que
    // encurtava ainda mais a margem. 30s cobre o pior caso observado com folga.
    const fim = Date.now() + (maxMs || 30000);
    // Backoff CRESCENTE: a propagação quase sempre resolve nas primeiras tentativas, e o
    // passo fixo de 1000ms fazia dormir 1s cheio mesmo faltando pouco. Começa curto e vai
    // abrindo, com teto em 1s (mantém o volume de sondagem parecido no caso lento, que é
    // o que importa pro rate limit). Rastreia: espera parada de ~2,3s no confirmar.
    const ESPERAS = [200, 400, 700, 1000];
    let tentativa = 0;
    while (Date.now() < fim) {
      try {
        const p = await fetch(`/products/${handle}.js?ts=${Date.now()}`, { headers: { Accept: 'application/json' }, cache: 'no-store' }).then((r) => (r.ok ? r.json() : null));
        // exige que a variante EXISTA e esteja available:true — senão o carrinho
        // mostra "Produto esgotou" (item.variant.available false durante a propagação).
        // E que a vitrine já sirva o corte, não a reserva: título de reserva ou preço
        // diferente do que o proxy mandou = ainda é a foto antiga (#6663).
        if (p && Array.isArray(p.variants) && p.variants.some((v) => String(v.id) === alvo && v.available
          && !ehReserva(v.title) && precoConfere(alvo, v.price))) return true;
      } catch (_) {}
      const espera = ESPERAS[Math.min(tentativa, ESPERAS.length - 1)];
      tentativa++;
      if (Date.now() + espera >= fim) break; // não dorme além do prazo à toa
      await new Promise((r) => setTimeout(r, espera));
    }
    return false;
  }

  // ── CACHE do pré-aquecimento ──────────────────────────────────────────────
  // Guarda { variantId, ready } por (produto+metragem). Quando pré-aquecida E já
  // disponível, o clique PULA resolverCorte + sondagem → add instantâneo.
  const warmCache = Object.create(null);
  const chaveWarm = (productId, metros) => `${String(productId).replace(/[^0-9]/g, '')}:${Number(metros).toFixed(1)}`;

  // Resolve a variante do corte e garante que está disponível, usando o cache.
  // Fast path: se já está no cache como ready, retorna na hora (sem rede).
  // Preparações EM VOO, por chave. Sem isso o Confirmar abria uma SEGUNDA sondagem do
  // zero enquanto o pré-aquecimento (disparado no clique do +) ainda sondava a mesma
  // metragem — duas esperas concorrentes pelo mesmo evento, ~1-2s jogados fora por ação.
  const emVoo = Object.create(null);

  async function prepararCorte(productId, metros, titulo, precoMetro, handle) {
    const k = chaveWarm(productId, metros);
    if (warmCache[k] && warmCache[k].ready) return warmCache[k]; // ⚡ instantâneo
    // já tem preparação rolando pra ESTA metragem → pega carona (ela começou antes,
    // então chega mais cedo do que recomeçar). Rastreia: sondagem duplicada.
    if (emVoo[k]) return emVoo[k];
    emVoo[k] = (async () => {
      const variantId = (warmCache[k] && warmCache[k].variantId) || await resolverCorte(productId, metros, titulo, precoMetro);
      warmCache[k] = { variantId, ready: false };
      const ready = await esperarVariantePronta(handle, variantId);
      // Não pronta: esquece a variante para a próxima tentativa perguntar ao proxy de
      // novo. Senão ela reusaria o preço esperado desta volta, e se a promoção mudou
      // nesse meio-tempo a sondagem recusaria para sempre, até recarregar a página.
      if (ready) warmCache[k] = { variantId, ready };
      else delete warmCache[k];
      return { variantId, ready };
    })();
    try {
      return await emVoo[k];
    } finally {
      // libera a chave: se falhou, a próxima tentativa recomeça limpa.
      delete emVoo[k];
    }
  }

  // Pré-aquece em BACKGROUND (cria + sonda + cacheia) — não bloqueia a UI. Dedup por
  // chave: não re-aquece o que já está ready nem o que já está em andamento.
  const preAquecendo = Object.create(null);
  function preAquecerCorte(productId, metros, titulo, precoMetro, handle) {
    const k = chaveWarm(productId, metros);
    if ((warmCache[k] && warmCache[k].ready) || preAquecendo[k]) return;
    preAquecendo[k] = true;
    prepararCorte(productId, metros, titulo, precoMetro, handle)
      .catch(() => { delete warmCache[k]; })
      .finally(() => { delete preAquecendo[k]; });
  }

  // expõe o núcleo pro JS do carrinho
  window.DecimalCut = { resolverCorte, prepararCorte, preAquecerCorte, getCartMeters, cartAdd, esperarVariantePronta, ehReserva, precoEsperado, precoConfere, brl, parseBR, fmtBR };

  // ---------- inicialização por produto ----------
  function init() {
    const btn = document.getElementById('add-to-cart-decimal');
    const input = document.getElementById('quantidade-decimal');
    const section = document.querySelector('.decimal-quantity-section');
    if (!btn || !input || !section) return;
    window.decimalQuantityInitialized = true;

    const cfg = {
      productId: btn.dataset.productId || document.querySelector('input[name="product-id"]')?.value,
      handle: (location.pathname.match(/\/products\/([^/?#]+)/) || [])[1] || '',
      titulo: btn.dataset.productTitle || document.title,
      // `trim` também aqui: o Liquid já limpa, mas o valor pode chegar por outro
      // caminho um dia e o espaço só aparece na frente da cliente, no checkout.
      sku: (btn.dataset.productSku || '').trim(),
      precoMetro: Number(btn.dataset.variantPrice || 0) / 100,
      compareMetro: btn.dataset.variantComparePrice ? Number(btn.dataset.variantComparePrice) / 100 : 0,
      cashbackPct: parseFloat(String(btn.dataset.cashbackPercentage || '0').replace(',', '.').replace(/[^\d.]/g, '')) || 0,
      pixPct: parseFloat(String(btn.dataset.pixDiscountPercentage || '0').replace(',', '.').replace(/[^\d.]/g, '')) || 0,
      step: Number(input.dataset.step || 0.1),
      originalQty: Number(btn.dataset.originalQuantity || btn.dataset.availableQuantity || 0),
    };
    const el = {
      current: document.getElementById('current-price'),
      original: document.getElementById('original-price'),
      pix: document.getElementById('pix-price'),
      cashback: document.getElementById('cashback-value'),
      metroText: section.querySelector('.metro-text'),
      stockQtd: section.querySelector('.stock-text .quantidade'),
      btnText: btn.querySelector('.button-text'),
      spinner: btn.querySelector('.spinner-container'),
      erro: garantirErro(),
    };

    // o snippet não renderiza o #quantidade-decimal-error (o JS antigo criava) — garantimos
    function garantirErro() {
      let e = document.getElementById('quantidade-decimal-error');
      if (!e) {
        const msgs = document.getElementById('quantidade-decimal-messages') || section;
        e = document.createElement('div');
        e.id = 'quantidade-decimal-error';
        e.className = 'quantidade-error-message';
        // A COR sai daqui de propósito: inline vence CSS, e com `color` fixo no elemento
        // o aviso de PROGRESSO herdava o vermelho de erro — justamente o que ele precisa
        // não parecer. Quem pinta agora é o CSS, por `data-tipo` (erro vermelho, info cinza).
        e.style.cssText = 'display:none;margin-top:4px;';
        msgs.appendChild(e);
      }
      return e;
    }

    // disponível dinâmico = original − metros já no carrinho
    let disponivel = Number(input.dataset.max || cfg.originalQty) || 0;
    let ultimoNoCarrinho = 0; // cache p/ prever o total do corte no pré-aquecimento
    async function atualizarDisponivel() {
      const noCarrinho = await getCartMeters(cfg.productId);
      ultimoNoCarrinho = noCarrinho;
      disponivel = Math.max(0, Math.round((cfg.originalQty - noCarrinho) * 10) / 10);
      input.dataset.max = String(disponivel);
      if (el.stockQtd) el.stockQtd.textContent = fmtBR(disponivel);
      const semEstoque = disponivel <= 0 || (disponivel < 0.5 && noCarrinho <= 0);
      btn.disabled = semEstoque;
      btn.style.opacity = semEstoque ? '0.6' : '';
      // reseta o texto NOS DOIS sentidos: senão, ao voltar o estoque (ex.: reduziu a
      // quantidade no carrinho), o botão reabilitava mas continuava "Indisponível".
      // Não sobrescreve enquanto está adicionando ("Adicionando…").
      if (el.btnText && el.btnText.textContent.trim() !== 'Adicionando…') {
        el.btnText.textContent = semEstoque ? 'Indisponível' : 'Comprar';
      }
      return noCarrinho;
    }

    // exibição de preço (porta as fórmulas do tema)
    function atualizarPreco() {
      const q = parseBR(input.value);
      const total = cfg.precoMetro * q;
      // #current-price tem o span aninhado .metro-text — reconstrói preservando "/metro" (só qty 1)
      if (el.current) el.current.innerHTML = brl(total) + (q === 1 ? '<span class="metro-text">/metro</span>' : '');
      if (el.original) el.original.textContent = cfg.compareMetro > cfg.precoMetro ? brl(cfg.compareMetro * q) : '';
      if (el.cashback && cfg.cashbackPct) el.cashback.textContent = brl(Math.floor(total * cfg.cashbackPct) / 100);
      // O texto do PIX é FIXO ("5% OFF no PIX"), escrito pelo Liquid — aqui só revelamos.
      // Escrever o valor em reais faria a página anunciar um preço 5% abaixo do que o feed
      // manda, e o Google Merchant reprova por "Preço incorreto". O porquê inteiro está no
      // comentário de `snippets/decimal_quantity_button.liquid`. ⛔ Não voltar a calcular.
      if (el.pix && cfg.pixPct) {
        el.pix.style.display = 'inline-block'; // o CSS esconde por padrão (display:none)
      }
    }

    // `tipo` separa AVISO (erro, vermelho) de INFORMAÇÃO (progresso, discreto). Sem essa
    // distinção, um aviso de progresso apareceria com cara de erro — o oposto do efeito.
    function mostrarErro(msg, tipo) {
      if (!el.erro) return;
      el.erro.textContent = msg || '';
      el.erro.dataset.tipo = tipo || 'erro';
      // TODA mensagem da PDP estava INVISÍVEL (comprovado em 23/07/2026): o JS criava o
      // elemento contando com `display`, mas o CSS do tema estiliza a mesma classe como
      // balão com `opacity:0; visibility:hidden` — que só abre com `.show` — e o contêiner
      // `.quantity-messages` nasce `display:none`. Resultado: "Máximo disponível: X m"
      // era gerado e ocupava 0×0 na tela; quem pedia acima do estoque clicava em Comprar
      // e não via NADA acontecer. Precisa mexer nos três: texto, `.show` e o contêiner.
      el.erro.classList.toggle('show', !!msg);
      el.erro.style.display = msg ? 'block' : 'none';
      const cont = el.erro.parentElement;
      if (cont && cont.classList.contains('quantity-messages')) {
        cont.style.display = msg ? 'block' : 'none';
      }
    }

    function setMetros(v) {
      const m = Math.max(0, Math.round(v * 10) / 10);
      input.value = fmtBR(m);
      atualizarPreco();
      preAquecer();
    }

    // PRÉ-AQUECIMENTO: quando a metragem se estabiliza, cria a variante E sonda até
    // ficar disponível, em BACKGROUND (com debounce), guardando no cache. Aí o clique
    // pega do cache e adiciona na hora. Debounce curto pra aquecer o quanto antes.
    let preAquecerTimer = null;
    function preAquecer() {
      clearTimeout(preAquecerTimer);
      preAquecerTimer = setTimeout(() => {
        const novo = parseBR(input.value);
        const total = Math.round((ultimoNoCarrinho + novo) * 10) / 10;
        // o teto (originalQty) já evitava criar variante absurda aqui; o que faltava era
        // espera maior: com 250ms, digitar "12" criava também "1.0 metros" no catálogo,
        // um valor de passagem que o cliente nunca pediu. Rastreia: variantes fantasma.
        if (total < 0.5 || total > cfg.originalQty + 0.001) return;
        preAquecerCorte(cfg.productId, total, cfg.titulo, cfg.precoMetro, cfg.handle);
      }, 1200);
    }

    // ---------- seletor ----------
    input.addEventListener('input', () => {
      let v = parseBR(input.value);
      if (v > disponivel) { v = disponivel; mostrarErro(`Máximo disponível: ${fmtBR(disponivel)} m`); }
      else mostrarErro('');
      atualizarPreco();
      preAquecer();
    });
    input.addEventListener('blur', () => {
      let v = parseBR(input.value);
      if (v < 0.5) v = 0.5;
      if (v > disponivel) v = disponivel;
      v = Math.round(v * 10) / 10; // arredonda ao passo 0,1
      setMetros(v);
      mostrarErro('');
    });
    section.querySelector('.quantity-button.plus')?.addEventListener('click', () => {
      const v = parseBR(input.value) + cfg.step;
      if (v > disponivel + 0.001) { mostrarErro(`Máximo disponível: ${fmtBR(disponivel)} m`); return; }
      setMetros(v); mostrarErro('');
    });
    section.querySelector('.quantity-button.minus')?.addEventListener('click', () => {
      const v = parseBR(input.value) - cfg.step;
      if (v < 0.5) { mostrarErro('Compra mínima 0,5 m'); return; }
      setMetros(v); mostrarErro('');
    });

    // ---------- adicionar ao carrinho (1 chamada) ----------
    btn.addEventListener('click', async (ev) => {
      ev.preventDefault();
      if (btn.dataset.processing === 'true') return;
      // A trava liga ANTES do primeiro await (16/10/2026): ligada depois da leitura do
      // carrinho, dois cliques em ~250 ms passavam juntos — cobrança em dobro (as duas
      // unidades se fundem numa linha de quantidade 2) ou o tecido apagado pelo
      // removeOutras do outro clique.
      btn.dataset.processing = 'true';
      btn.disabled = true;
      const novo = parseBR(input.value);
      let cartAntes = null;
      try { cartAntes = await fetch('/cart.js', { headers: { Accept: 'application/json' }, cache: 'no-store' }).then((r) => r.json()); } catch (_) {}
      const noCarrinho = await getCartMeters(cfg.productId, cartAntes || undefined);
      const chavesAntes = cartAntes
        ? (cartAntes.items || []).filter((it) => String(it.product_id) === String(cfg.productId)).map((it) => it.key)
        : null;
      const total = Math.round((noCarrinho + novo) * 10) / 10;
      const soltar = () => { btn.dataset.processing = 'false'; btn.disabled = false; };
      if (total < 0.5) { soltar(); mostrarErro('Compra mínima 0,5 m'); return; }
      if (total > cfg.originalQty + 0.001) { soltar(); mostrarErro(`Máximo disponível: ${fmtBR(cfg.originalQty)} m`); return; }

      const textoOrig = el.btnText ? el.btnText.textContent : '';
      if (el.btnText) el.btnText.textContent = 'Adicionando…';
      el.spinner?.classList.add('show');
      mostrarErro('');

      // FEEDBACK ESCALONADO. A PRIMEIRA venda de uma metragem espera a Shopify publicar
      // a variante recém-criada — medido em 23/07/2026: ~5,5s até o /cart/add aceitar, e
      // já vimos 24s no pior caso. É latência da plataforma, não dá para encurtar aqui.
      // O problema é que "Adicionando…" parado por 8s LÊ COMO SITE TRAVADO e o cliente
      // desiste ou clica de novo. Dizer o que está acontecendo não acelera nada, mas
      // transforma "quebrou" em "está trabalhando". Metragem já existente resolve em ~2s
      // e nem chega no primeiro aviso. Rastreia: espera da metragem inédita.
      const AVISOS = [
        [1800, `Preparando seu corte de ${fmtBR(total)} m…`],
        [4500, 'Primeira vez nesta medida — só mais um instante…'],
      ];
      const timers = AVISOS.map(([ms, txt]) => setTimeout(() => mostrarErro(txt, 'info'), ms));
      const limparAvisos = () => { timers.forEach(clearTimeout); };

      try {
        // prepararCorte usa o cache do pré-aquecimento: se a metragem já foi aquecida
        // e está disponível, retorna NA HORA (sem re-resolver nem re-sondar) → add rápido.
        const prep = await prepararCorte(cfg.productId, total, cfg.titulo, cfg.precoMetro, cfg.handle);
        if (!prep.ready) { const err = new Error('nao-propagou'); err.naoPropagou = true; throw err; }
        await cartAdd(prep.variantId, total, cfg.precoMetro, cfg.sku);
        if (noCarrinho > 0) await removeOutras(cfg.productId, prep.variantId, chavesAntes); // funde no total acumulado
        await atualizarDisponivel();
        document.dispatchEvent(new CustomEvent('cart:updated'));
        // Dispara o fluxo COMPLETO do drawer (abre + refresh do body + subtotal +
        // contador + desconto). Antes chamávamos open() direto, que pulava o
        // updateSubtotalFromCart → subtotal ficava velho (0,00). Rastreia: bug subtotal.
        document.dispatchEvent(new CustomEvent('cartItemAdded', { detail: { productId: cfg.productId } }));
        limparAvisos();
        mostrarErro(''); // some com o aviso de progresso; no erro, o catch escreve por cima
      } catch (e) {
        limparAvisos(); // senão um aviso de progresso sobrescreveria a mensagem de erro
        if (e && e.semEstoque) {
          // Mensagem do SERVIDOR, com o número de agora. Depois de mostrar, relê o
          // disponível: a página estava exibindo um estoque velho — foi por isso que
          // o clique chegou até aqui — e deixá-la mentindo faria o cliente tentar de
          // novo e levar a mesma recusa.
          mostrarErro(e.message);
          atualizarDisponivel().catch(() => {});
        }
        else if (e && e.naoPropagou) mostrarErro('O corte está sendo preparado. Aguarde alguns segundos e tente novamente.');
        else if (e && e.rateLimited) mostrarErro(`Muitas tentativas. Aguarde ~${e.retryAfter}s e tente novamente.`);
        else mostrarErro('Não foi possível adicionar agora. Tente novamente.');
        console.error('[decimal] ATC falhou:', e?.message);
      } finally {
        limparAvisos();
        btn.dataset.processing = 'false';
        btn.disabled = false;
        if (el.btnText) el.btnText.textContent = textoOrig || 'Comprar';
        el.spinner?.classList.remove('show');
      }
    });

    // estado inicial
    atualizarPreco();
    atualizarDisponivel();
    document.addEventListener('cart:updated', atualizarDisponivel);
    // PRÉ-AQUECE a metragem padrão já no load: a variante é criada enquanto o cliente
    // lê a página (10-60s), então quando ele clica Comprar já propagou → instantâneo.
    // Como variantes são reusadas entre visitantes, depois do 1º acesso fica quente.
    preAquecer();
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
